// Kamera mapy hero D sterowana przewijaniem (paczka HD, runda 41-D-R2).
//
// Właściciel 22.09: „oddalenie żeby działało automatycznie – gdy scrolluje się z dołu na górę.
// a przybliżenie z góry na dół”. Czyli: na samej górze strony widać dokładnie klatkę D-01, w dół
// kamera oddala się aż do ~100 kont, w górę wraca. Ruch zależy WYŁĄCZNIE od pozycji przewijania
// i jest w pełni odwracalny: bez zegara, bez dociągania, bez autoplay.
//
// JEDEN MIANOWNIK na obu ścieżkach: p = (scrollY − góra pasa) / (wysokość pasa − wysokość ekranu
// przypiętego). Nigdy `innerHeight` (telefon ze schowanym paskiem adresu ma tam lvh, a ekran ma
// svh) i nigdy `offset*` (jsdom zwraca 0). Ten sam pomiar daje zakres animacji, LOD, `.on`,
// kamerę ścieżki zapasowej i próg przyklejenia nagłówka.
//
// ŚCIEŻKA GŁÓWNA = Web Animations API z `ScrollTimeline`: 21 klatek camera(i / 20) na jednej
// warstwie `.mp-fm-cam`, zakres w px dokumentu z pomiaru. Zero reguł klatek kluczowych w CSS (bramka
// `ds-system-gates` wpuszcza je tylko do `ds/motion.css`) i zero reguł animacji w CSS. Na tej
// ścieżce ani React, ani ten hook nie piszą `style.transform`: przewija kompozytor.
// ŚCIEŻKA ZAPASOWA (brak `ScrollTimeline`): pasywny `scroll` + `requestAnimationFrame`, w klatce
// wyłącznie `transform` i klasy przy przekroczeniu progu.
//
// KLASY OKNA pisze wyłącznie ten hook (`classList` na refach, porównanie z poprzednim stanem):
// `intro`, `mp-fm-cut`, `z1..z3`, `mp-fm-sda` / `mp-fm-js`, a `.on` na węzłach kont. W JSX tych
// klas nie ma, bo ponowny render Reacta nadpisałby atrybut `class`; żadnego `setState` na klatkę.
//
// Moduł nie dotyka `window` przy imporcie. Brak `matchMedia` (jsdom) = stan statyczny jak przy
// ograniczonym ruchu: bez przypięcia, bez nasłuchu, bez wyjątku.
//
// RUCH WEJŚCIA TYLKO NA PUSTYM MIEJSCU (dogrywka HD2): gdy statyczny HTML (snapshot prerendera) już
// namalował mapę w stanie p = 0, montaż ją przejmuje bez `.intro` (`entry` = false). Inaczej mapa
// widoczna od pierwszego malowania znikałaby przy montażu i wchodziła od nowa (pomiar HD, `dist`
// przy CPU × 4: widoczna ~0,5 s, znika, potem 2,5 s wejścia).

import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import { camera, keyframes, lodOf } from '../lib/landingTree'
import type { World } from '../lib/landingTree'

/** Próg układu poziomego hero i orientacji mapy (brief HD, B4). Ta sama liczba stoi w CSS. */
export const HERO_WIDE_PX = 1336
export const HERO_WIDE_QUERY = `(min-width: ${HERO_WIDE_PX}px)`
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
/** Wysokość okna mapy przy p = 0 bez skalowania: 300 (pion), 520 (poziom). */
const BASE_H = { v: 300, h: 520 } as const

type MediaState = { motion: boolean; wide: boolean }

function readMedia(): MediaState {
  const mm = typeof window !== 'undefined' ? window.matchMedia : undefined
  if (typeof mm !== 'function') return { motion: false, wide: false }
  return { motion: !mm.call(window, REDUCED_MOTION_QUERY).matches, wide: mm.call(window, HERO_WIDE_QUERY).matches }
}

/**
 * Czy wolno przypiąć hero (ruch nie jest ograniczony) i czy mapa jest pozioma.
 * Czytane synchronicznie w inicjalizatorze stanu, żeby pierwszy render klienta nie przeskoczył
 * o dwa ekrany po montażu; nasłuch `change` dochodzi w efekcie.
 */
export function useHeroMedia(): MediaState {
  const [state, setState] = useState(readMedia)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const lists = [window.matchMedia(REDUCED_MOTION_QUERY), window.matchMedia(HERO_WIDE_QUERY)]
    const onChange = () => setState((prev) => {
      const next = readMedia()
      return next.motion === prev.motion && next.wide === prev.wide ? prev : next
    })
    for (const l of lists) l.addEventListener?.('change', onChange)
    onChange()
    return () => { for (const l of lists) l.removeEventListener?.('change', onChange) }
  }, [])
  return state
}

/** Postęp przewinięcia pasa i zakres drogi kamery w px dokumentu (jeden pomiar, B6). */
export function heroProgress(pin: Element, screen: Element): { p: number; start: number; end: number } {
  const r = pin.getBoundingClientRect()
  const start = r.top + window.scrollY
  const span = r.height - screen.getBoundingClientRect().height
  const end = start + Math.max(span, 0)
  const p = span > 0 ? Math.min(1, Math.max(0, (window.scrollY - start) / span)) : 0
  return { p, start, end }
}

export interface HeroZoomRefs {
  pin: RefObject<HTMLElement | null>
  screen: RefObject<HTMLElement | null>
  win: RefObject<HTMLElement | null>
  cam: RefObject<HTMLElement | null>
  fit: RefObject<HTMLElement | null>
}

type ScrollTimelineCtor = new (o: { source: Element; axis: 'block' }) => AnimationTimeline

/**
 * Kamera, LOD, pojawianie się kont, skala niskiego okna i ruch wejścia dla okna mapy hero.
 * `enabled` = ruch dozwolony i `matchMedia` jest; inaczej hook nic nie robi (stan p = 0).
 * `entry` = false: mapa już stoi (snapshot prerendera), więc przy p = 0 bez ruchu wejścia.
 */
export function useHeroZoom(refs: HeroZoomRefs, world: World, enabled: boolean, entry = true): void {
  useEffect(() => {
    const pin = refs.pin.current, screen = refs.screen.current, win = refs.win.current, cam = refs.cam.current
    const fit = refs.fit.current
    if (!enabled || !pin || !screen || !win || !cam || typeof window === 'undefined') return

    const Timeline = (window as unknown as { ScrollTimeline?: ScrollTimelineCtor }).ScrollTimeline
    const sda = typeof Timeline === 'function' && typeof cam.animate === 'function'
    win.classList.add(sda ? 'mp-fm-sda' : 'mp-fm-js')

    // węzły kont dochodzących: grupa w SVG świata + węzeł DOM (awatar, nick), po indeksie konta
    const nodes = new Map<number, Element[]>()
    for (const el of win.querySelectorAll<HTMLElement | SVGElement>('[data-i]')) {
      const i = Number(el.dataset.i)
      nodes.set(i, [...(nodes.get(i) ?? []), el])
    }
    const extra = world.accounts.filter((a) => !a.d)
    const shown = new Map<number, boolean>()

    let range = heroProgress(pin, screen)
    let anim: Animation | null = null
    let lod = -1
    let cut = false
    let frame = 0

    const progress = () => {
      const span = range.end - range.start
      return span > 0 ? Math.min(1, Math.max(0, (window.scrollY - range.start) / span)) : 0
    }
    const apply = (p: number) => {
      if (!sda) cam.style.transform = camera(world, p).css
      const l = lodOf(p)
      if (l !== lod) {
        win.classList.toggle('z1', l >= 1)
        win.classList.toggle('z2', l >= 2)
        win.classList.toggle('z3', l >= 3)
        lod = l
      }
      for (const a of extra) {
        const on = p >= a.rev
        if (shown.get(a.i) === on) continue
        shown.set(a.i, on)
        for (const el of nodes.get(a.i) ?? []) el.classList.toggle('on', on)
      }
      // Pierwsze przewinięcie kończy ruch wejścia: stan końcowy od razu, kamerę przejmuje przewijanie.
      if (p > 0 && !cut) {
        cut = true
        win.classList.add('mp-fm-cut')
        win.classList.remove('intro')
      }
    }
    // Skala niskiego okna: k = wysokość okna / wysokość bez skalowania (CSS w Safari 16 nie
    // podzieli długości przez długość). Okno zmienia wysokość także bez `resize`: wysokość
    // w przypięciu zależy od paska cookie (`--cookie-bar-h`), który wchodzi i schodzi.
    const scale = () => {
      if (!fit) return
      const k = win.getBoundingClientRect().height / BASE_H[world.orient]
      fit.style.setProperty('--mp-fm-k', String(k > 0 ? Math.round(k * 10000) / 10000 : 1))
    }
    const layout = () => {
      range = heroProgress(pin, screen)
      scale()
      if (!sda) return
      anim?.cancel()
      anim = cam.animate(keyframes(world), {
        timeline: new (Timeline as ScrollTimelineCtor)({ source: document.documentElement, axis: 'block' }),
        rangeStart: `${range.start}px`,
        rangeEnd: `${range.end}px`,
        fill: 'both',
        easing: 'linear',
      } as KeyframeAnimationOptions)
    }
    // `queued` zamiast samego numeru klatki: klatka wywołana od razu (atrapa w teście) nie może
    // zostawić numeru, który zablokuje każdą następną.
    let queued = false
    const read = () => { queued = false; frame = 0; apply(progress()) }
    const onScroll = () => {
      if (queued) return
      queued = true
      frame = requestAnimationFrame(read)
    }
    const onResize = () => { layout(); apply(progress()) }

    layout()
    const p0 = progress()
    apply(p0)
    let introFrame = 0
    if (p0 === 0 && entry) {
      // Ruch wejścia z D na przejściach CSS: `.intro` trzyma stan startowy dwie klatki.
      win.classList.add('intro')
      introFrame = requestAnimationFrame(() => { introFrame = requestAnimationFrame(() => { introFrame = 0; win.classList.remove('intro') }) })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
    const sized = typeof ResizeObserver === 'function' ? new ResizeObserver(scale) : null
    sized?.observe(win)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
      sized?.disconnect()
      if (queued) cancelAnimationFrame(frame)
      if (introFrame) cancelAnimationFrame(introFrame)
      anim?.cancel()
      cam.style.transform = ''
      fit?.style.removeProperty('--mp-fm-k')
      win.classList.remove('intro', 'mp-fm-cut', 'z1', 'z2', 'z3', 'mp-fm-sda', 'mp-fm-js')
      for (const list of nodes.values()) for (const el of list) el.classList.remove('on')
    }
  }, [refs, world, enabled, entry])
}
